import React from 'react';
import classNames from 'classnames';

import IconArrowHead from '../IconArrowHead/IconArrowHead';

import css from './ProgressStep.module.css';

/**
 * Step navigation for a multi-step form. Pass step content as children.
 *
 * @component
 * @param {Object} props
 * @param {string} [props.className]
 * @param {string} [props.rootClassName]
 * @param {number} [props.currentStep]
 * @param {Array<{ title: string, disabled?: boolean }>} [props.steps]
 * @param {Function} props.onStepChange
 * @param {JSX.Element} props.children
 * @returns {JSX.Element}
 */
const ProgressStep = props => {
  const {
    className,
    rootClassName,
    currentStep = 1,
    steps = [],
    onStepChange,
    children,
    ...rest
  } = props;

  const classes = classNames(rootClassName || css.root, className);

  const handleStepKeyDown = (event, index, disabled) => {
    if (disabled) {
      return;
    }
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onStepChange(index + 1);
    }
  };

  return (
    <div className={classes} {...rest}>
      <div className={css.container}>
        <div className={css.header}>
          <div className={css.progressNav}>
            {steps.map((step, index) => (
              <div
                key={step.title}
                className={classNames(css.progressItem, { [css.disabled]: step.disabled })}
                role="button"
                tabIndex={step.disabled ? -1 : 0}
                onClick={() => !step.disabled && onStepChange(index + 1)}
                onKeyDown={event => handleStepKeyDown(event, index, step.disabled)}
              >
                <span
                  className={classNames(css.progressText, {
                    [css.disabled]: step.disabled,
                    [css.active]: index === currentStep - 1 && !step.disabled,
                    [css.completed]: index < currentStep - 1,
                  })}
                >
                  {step.title}
                </span>
                {index < steps.length - 1 ? (
                  <IconArrowHead
                    className={classNames(css.arrowIcon, {
                      [css.disabled]: currentStep <= index + 1,
                    })}
                    direction="right"
                    size="small"
                  />
                ) : null}
              </div>
            ))}
          </div>
        </div>

        <div className={css.content}>{children}</div>
      </div>
    </div>
  );
};

export default ProgressStep;
